import {
  type ClaudiaAttachmentCategory,
  describeClaudiaAttachmentSupportedTypes,
  formatArtifactMaxFileSize,
  getArtifactMaxFileSizeBytes,
  resolveClaudiaAttachmentFileType,
} from "@repo/shared";
import { isHeicImage, type PreparedImageAsset, prepareImageAsset } from "./image-processing";

export type PreparedClaudiaImageAttachment = {
  kind: "image";
  category: "image";
  mimeType: string;
  asset: PreparedImageAsset;
};

export type PreparedClaudiaDocumentAttachment = {
  kind: "document";
  category: Exclude<ClaudiaAttachmentCategory, "image">;
  mimeType: string;
  file: File;
};

export type PreparedClaudiaAttachmentFile =
  | PreparedClaudiaImageAttachment
  | PreparedClaudiaDocumentAttachment;

function withCanonicalMimeType(file: File, mimeType: string): File {
  if (file.type === mimeType) {
    return file;
  }
  return new File([file], file.name, {
    type: mimeType,
    lastModified: file.lastModified,
  });
}

export function isSupportedClaudiaAttachment(file: File): boolean {
  return Boolean(resolveClaudiaAttachmentFileType(file.name, file.type));
}

export async function prepareClaudiaAttachmentFile(
  file: File
): Promise<PreparedClaudiaAttachmentFile> {
  if (isHeicImage(file)) {
    throw new Error("HEIC images are not supported yet. Use PNG, JPEG, WebP, or a static GIF.");
  }

  const resolved = resolveClaudiaAttachmentFileType(file.name, file.type);
  if (!resolved) {
    // The list of what is taken is the registry's to state, not this file's: a type enabled
    // or disabled changes the sentence with it.
    throw new Error(
      `This file type is not supported. ${describeClaudiaAttachmentSupportedTypes()}`
    );
  }

  // The one ceiling: a file attached in chat is registered as a project artifact and read by
  // the same pipeline the Documents screen feeds, so it is measured against the same number.
  if (file.size > getArtifactMaxFileSizeBytes(file.name, file.type)) {
    throw new Error(
      `Files of this type must be smaller than ${formatArtifactMaxFileSize(file.name, file.type)}.`
    );
  }

  const canonicalFile = withCanonicalMimeType(file, resolved.canonicalMimeType);
  if (resolved.category === "image") {
    const asset = await prepareImageAsset(canonicalFile);
    return {
      kind: "image",
      category: "image",
      mimeType: asset.original.type || resolved.canonicalMimeType,
      asset,
    };
  }

  return {
    file: canonicalFile,
    kind: "document",
    category: resolved.category,
    mimeType: resolved.canonicalMimeType,
  };
}
